import React, { FC, useState } from 'react';

import {
  OnboardingDraft,
  OnboardingDraftPitch,
  ValidationIssue,
} from '@intelligent-play-v2/domain';
import { Button, ButtonType } from '~/components/button';

import { ConfirmationBox } from './ConfirmationBox';
import { FacilityImagePicker } from './FacilityImagePicker';

interface OnboardingCommitPreviewProps {
  draft: OnboardingDraft;
  /** Blocking problems. Commit must be impossible while this is non-empty. */
  errors: ValidationIssue[];
  /** Non-blocking, but the reviewer must tick to say they have read them. */
  warnings: ValidationIssue[];
  /** True when at least one pitch's maintenance schedules are still byte-identical to the server default template. */
  hasUntouchedDefaultSchedules: boolean;
  /**
   * The overview image to upload once commit succeeds. Held in memory by the page, like
   * the rest of the draft — nothing reaches the bucket unless the facility is created.
   */
  imageFile: File | null;
  imagePreviewUrl: string | null;
  onImageFileChange: (file: File | null) => void;
  onCommit: () => void;
  isCommitting: boolean;
  onBack: () => void;
}

const pluralise = (count: number, singular: string, plural: string): string =>
  `${count} ${count === 1 ? singular : plural}`;

const sumOverPitches = (
  pitches: OnboardingDraftPitch[],
  countOf: (pitch: OnboardingDraftPitch) => number
): number => pitches.reduce((total, pitch) => total + countOf(pitch), 0);

/** e.g. `#412 (6mm)`; an unassigned id or lens spec is shown as a dash rather than omitted. */
const describeSensor = (
  id: number | null,
  lensSpecMillimeters: number | null
): string => {
  const idLabel = id === null ? '#—' : `#${id}`;
  const lensLabel =
    lensSpecMillimeters === null ? '—' : `${lensSpecMillimeters}mm`;
  return `${idLabel} (${lensLabel})`;
};

/**
 * Step 3 of onboarding: the last look before rows are written.
 *
 * Field-by-field review happened in step 2, so this is deliberately a summary and not a
 * second review — counts and a one-row-per-pitch table to catch a wrong facility or a
 * missing pitch at a glance, with the full draft tucked behind an expander for the rare
 * case where the reviewer wants to check a value they cannot see here.
 */
export const OnboardingCommitPreview: FC<OnboardingCommitPreviewProps> = ({
  draft,
  errors,
  warnings,
  hasUntouchedDefaultSchedules,
  imageFile,
  imagePreviewUrl,
  onImageFileChange,
  onCommit,
  isCommitting,
  onBack,
}) => {
  const [isImageConfirmed, setIsImageConfirmed] = useState(false);
  const [areWarningsConfirmed, setAreWarningsConfirmed] = useState(false);
  const [areSchedulesConfirmed, setAreSchedulesConfirmed] = useState(false);
  const [isDetailShown, setIsDetailShown] = useState(false);

  const { facility, pitches } = draft;
  const imageFileName = `facility${facility.id}.png`;

  const deviceCount = sumOverPitches(pitches, pitch => pitch.sensors.length);
  const maintenanceScheduleCount = sumOverPitches(
    pitches,
    pitch => pitch.maintenanceSchedules.length
  );
  const snapshotScheduleCount = sumOverPitches(
    pitches,
    pitch => pitch.snapshotTimes.length
  );

  const countsLine = [
    '1 facility',
    pluralise(pitches.length, 'pitch', 'pitches'),
    pluralise(deviceCount, 'device', 'devices'),
    // One threshold row per pitch, so the pitch count is the threshold count.
    pluralise(pitches.length, 'alert threshold', 'alert thresholds'),
    pluralise(
      maintenanceScheduleCount,
      'maintenance schedule',
      'maintenance schedules'
    ),
    pluralise(snapshotScheduleCount, 'snapshot schedule', 'snapshot schedules'),
  ].join(', ');

  const hasErrors = errors.length > 0;
  const isWarningTickOutstanding = warnings.length > 0 && !areWarningsConfirmed;
  const isScheduleTickOutstanding =
    hasUntouchedDefaultSchedules && !areSchedulesConfirmed;
  // a chosen file will be uploaded on commit; without one, the manual tick stands in
  const isImageOutstanding = !imageFile && !isImageConfirmed;
  const isCommitDisabled =
    isCommitting ||
    hasErrors ||
    isImageOutstanding ||
    isWarningTickOutstanding ||
    isScheduleTickOutstanding;

  return (
    <div className="flex flex-col space-y-5">
      <div>
        <p className="text-xl font-semibold">Ready to create</p>
        <p className="mt-1 text-base text-primary-500">
          Creates: {countsLine}.
        </p>
      </div>

      <div className="p-4 bg-white border rounded border-primary-250">
        <p className="text-base">
          <span className="font-semibold">Facility {facility.id}</span>
          {` — ${facility.name}`}
        </p>
        <p className="mt-1 text-sm text-primary-500">
          Overview image path: <code className="text-sm">{imageFileName}</code>
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-base text-left">
          <thead>
            <tr className="text-sm text-primary-500">
              <th className="px-3 py-2 font-semibold whitespace-nowrap">Id</th>
              <th className="px-3 py-2 font-semibold whitespace-nowrap">
                Name
              </th>
              <th className="px-3 py-2 font-semibold whitespace-nowrap">No.</th>
              <th className="px-3 py-2 font-semibold whitespace-nowrap">
                Sports
              </th>
              <th className="px-3 py-2 font-semibold whitespace-nowrap">
                Sensors (lens)
              </th>
              <th className="px-3 py-2 font-semibold whitespace-nowrap">
                Snapshot times
              </th>
            </tr>
          </thead>
          <tbody>
            {pitches.map(pitch => (
              <tr key={pitch.id} className="border-t border-primary-250">
                <td className="px-3 py-2 whitespace-nowrap">{pitch.id}</td>
                <td className="px-3 py-2">{pitch.name}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {pitch.facilityPitchNumber}
                </td>
                <td className="px-3 py-2">{pitch.sports.join(' / ')}</td>
                <td className="px-3 py-2">
                  {pitch.sensors
                    .map(sensor =>
                      describeSensor(sensor.id, sensor.lensSpecMillimeters)
                    )
                    .join(', ')}
                </td>
                <td className="px-3 py-2">{pitch.snapshotTimes.join(', ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div>
        <button
          type="button"
          onClick={() => setIsDetailShown(!isDetailShown)}
          className="text-base font-semibold text-blue"
        >
          {isDetailShown ? 'Hide full draft detail' : 'Show full draft detail'}
        </button>
        {isDetailShown && (
          <pre className="p-3 mt-2 overflow-auto text-sm rounded max-h-80 bg-primary-100">
            {JSON.stringify(draft, null, 2)}
          </pre>
        )}
      </div>

      <div className="flex flex-col space-y-3">
        <FacilityImagePicker
          facilityId={facility.id}
          imageFile={imageFile}
          imagePreviewUrl={imagePreviewUrl}
          onImageFileChange={onImageFileChange}
        />

        {!imageFile && (
          <ConfirmationBox
            title={`I'll upload ${imageFileName} to the bucket myself`}
            description={
              <>
                {
                  'No image is attached, so commit will not upload one. Put the '
                }
                {'overview image in the '}
                <code className="text-sm">smart-pitches-facility-images</code>
                {' bucket as '}
                <code className="text-sm">{imageFileName}</code>
                {' manually, or attach it above instead.'}
              </>
            }
            isChecked={isImageConfirmed}
            onChange={setIsImageConfirmed}
          />
        )}

        {warnings.length > 0 && (
          <ConfirmationBox
            title={`I've reviewed these ${pluralise(
              warnings.length,
              'warning',
              'warnings'
            )}`}
            description={
              <ul className="space-y-1">
                {warnings.map((warning, index) => (
                  <li key={`${warning.path}-${index}`}>
                    {!!warning.path && (
                      <code className="mr-1 text-sm">{warning.path}</code>
                    )}
                    {warning.message}
                  </li>
                ))}
              </ul>
            }
            isChecked={areWarningsConfirmed}
            onChange={setAreWarningsConfirmed}
          />
        )}

        {hasUntouchedDefaultSchedules && (
          <ConfirmationBox
            title="The default maintenance schedules are right for this facility"
            description="At least one pitch still carries the default template exactly as generated. Confirm the procedures, frequencies and maintainers suit this site."
            isChecked={areSchedulesConfirmed}
            onChange={setAreSchedulesConfirmed}
          />
        )}
      </div>

      {hasErrors && (
        <div className="p-4 border rounded bg-red-lightest border-red">
          <p className="text-base font-semibold text-red-dark">
            {`Commit is blocked by ${pluralise(
              errors.length,
              'error',
              'errors'
            )}. Go back to review and fix them.`}
          </p>
          <ul className="mt-2 space-y-1 text-sm text-red-dark">
            {errors.map((error, index) => (
              <li key={`${error.path}-${index}`}>
                {!!error.path && (
                  <code className="mr-1 text-sm">{error.path}</code>
                )}
                {error.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex justify-end space-x-3">
        <div className="w-48">
          <Button
            text="Back to review"
            type={ButtonType.Outline}
            onClick={onBack}
            disabled={isCommitting}
          />
        </div>
        <div className="w-48">
          <Button
            text="Create facility"
            onClick={onCommit}
            isLoading={isCommitting}
            disabled={isCommitDisabled}
            disabledStyle="light"
          />
        </div>
      </div>
    </div>
  );
};
